import type { OverviewCage } from '@/domain/client';
import type { SiteOccupiedPen } from '@/domain/fishGroups';

type PenSource = Pick<OverviewCage, 'cageId' | 'cageName' | 'activeBioscopes'>;

/** The overview's pens in use, as `OverviewLocation` groups them. */
interface SitePenSource {
    activeCages: readonly PenSource[];
    inactiveCages: readonly PenSource[];
    emptyCages: readonly PenSource[];
}

/** A pen of the site, with the bioscopes in it and the group holding it, if any. */
export interface SitePen {
    cageId: number;
    /** Real name, for requests and Suggest. */
    cageName: string;
    /** Masked in demo mode. */
    displayName: string;
    bioscopeIds: number[];
    occupant: SiteOccupiedPen | null;
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/**
 * Every pen of the site from the client overview, each with its occupant from the site-wide
 * occupied pens (any stocking or none), sorted by name.
 */
export function sitePens(
    location: SitePenSource | null,
    occupied: readonly SiteOccupiedPen[],
    penDisplayName: (cageId: number, cageName: string) => string
): SitePen[] {
    if (!location) return [];
    const occupantByCage = new Map(occupied.map((pen) => [pen.cageId, pen]));
    const seen = new Set<number>();
    // Not the historical pens: nothing can be moved into a pen that is no longer in use.
    return [...location.activeCages, ...location.inactiveCages, ...location.emptyCages]
        .filter((cage) => {
            if (seen.has(cage.cageId)) return false;
            seen.add(cage.cageId);
            return true;
        })
        .map((cage) => ({
            cageId: cage.cageId,
            cageName: cage.cageName,
            displayName: penDisplayName(cage.cageId, cage.cageName),
            bioscopeIds: [...cage.activeBioscopes].sort((a, b) => a - b),
            occupant: occupantByCage.get(cage.cageId) ?? null,
        }))
        .sort((a, b) => collator.compare(a.cageName, b.cageName));
}
